import { Chip, MenuItem, Select, Stack, Typography, useTheme } from '@mui/material';
import { ComponentProps, FC } from 'react';
import { LoadableSelect } from '../../form/LoadableSelect';
import { Add } from '@mui/icons-material';
import { FormattedMessage } from 'react-intl';
import { useActionPriorityOptions } from './useActionPriorityOptions';
import { getNightOrDayPalette } from '../../utils/getNightOrDayPalette';

type PrioritySelectProps = ComponentProps<typeof Select<string>> &
	Readonly<{
		loading?: boolean;
	}>;

export const PrioritySelect: FC<PrioritySelectProps> = ({ value, loading, ...props }) => {
	const theme = useTheme();
	const { priorityOptions } = useActionPriorityOptions();

	return (
		<LoadableSelect
			variant={'standard'}
			loading={loading}
			value={value || ''}
			renderValue={(value) => {
				if (loading) {
					return (
						<Typography>
							<FormattedMessage id={'common.loading'} />
						</Typography>
					);
				}

				const option = priorityOptions.find((o) => (o.value as string) === value);

				if (option) {
					return <PriorityItem {...option} />;
				}

				return (
					<Stack direction={'row'} gap={1} alignItems={'center'} sx={{ color: theme.palette.secondary.main }}>
						<Add sx={{ fontSize: '18px' }} />
						<Typography fontSize={'14px'}>
							<FormattedMessage id={'opportunity.detail.priority.placeholder'} />
						</Typography>
					</Stack>
				);
			}}
			sx={{
				'&::before': {
					content: 'none',
				},
				minHeight: '33px',
			}}
			IconComponent={() => null}
			{...props}
		>
			{priorityOptions.map((option) => (
				<MenuItem key={option.value} value={option.value}>
					<PriorityItem {...option} />
				</MenuItem>
			))}
		</LoadableSelect>
	);
};

export const PriorityItem: FC<ReturnType<typeof useActionPriorityOptions>['priorityOptions'][number]> = ({ text, color }) => {
	const theme = useTheme();

	return (
		<Chip key={text} label={text} size={'small'} sx={{ color: theme.palette[getNightOrDayPalette(theme)][0], backgroundColor: color }} />
	);
};
